import * as React from 'react';
import { Switch as ChakraSwitch, Field, Box, HStack } from '@chakra-ui/react';

export interface SwitchProps {
  label?: string;
  description?: string;
  error?: string;
  hint?: string;
  containerClassName?: string;
  _switchSize?: 'sm' | 'md' | 'lg';
  required?: boolean;
  disabled?: boolean;
  defaultChecked?: boolean;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  id?: string;
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      description,
      error,
      hint,
      containerClassName,
      _switchSize = 'md',
      required,
      disabled,
      defaultChecked,
      checked,
      onChange,
      id,
      ...props
    },
    _ref
  ) => {
    // Generate ID for accessibility - always call React.useId() to comply with Rules of Hooks
    const generatedId = React.useId();
    const switchId = id || generatedId;

    // Determine field status
    const invalid = !!error;

    // Map sizes (currently not used as ChakraSwitch doesn't support size prop)
    // const sizeMap = {
    //   sm: 'sm',
    //   md: 'md',
    //   lg: 'lg',
    // };

    // Filter out Chakra-specific props that shouldn't be passed to DOM
    const chakraPropsToFilter = [
      'invalid',
      'borderWidth',
      'colorScheme',
      'variant',
      'size',
      'orientation',
      'spacing',
      'isFitted',
      'align',
      'justify',
      'wrap',
      'shouldWrapChildren',
      'direction',
      'divider',
      '_hover',
      '_active',
      '_focus',
      '_disabled',
      '_checked',
      '_invalid',
      '_readOnly',
      '_required',
      '_loading',
      '_selected',
      '_expanded',
      '_highlighted',
      '_groupHover',
      '_groupActive',
      '_groupDisabled',
      '_groupFocus',
      '_groupChecked',
      '_groupInvalid',
      '_groupReadOnly',
      '_groupRequired',
      '_groupLoading',
      '_groupSelected',
      '_groupExpanded',
      '_groupHighlighted',
      '_focusVisible',
      '_focusWithin',
      '_hoverAndFocus',
      '_activeAndFocus',
      '_focusWithinVisible',
      '_group',
      '_peer',
      '_ancestor',
      '_parent',
      '_child',
      '_descendant',
      'css',
      'sx',
      '__css',
      '__sx',
    ];

    const filteredProps: Record<string, unknown> = {};
    Object.keys(props).forEach((key) => {
      if (!chakraPropsToFilter.includes(key)) {
        filteredProps[key as keyof typeof props] = props[key as keyof typeof props];
      }
    });

    // Switch control styles for visibility
    const switchControlStyle = {
      backgroundColor: 'gray.300',
      _checked: {
        backgroundColor: 'blue.600',
      },
      _hover: {
        backgroundColor: 'gray.400',
      },
      _disabled: {
        backgroundColor: 'gray.200',
        opacity: 0.6,
      },
    };

    // Switch thumb styles
    const switchThumbStyle = {
      backgroundColor: 'white',
      boxShadow: 'sm',
    };

    return (
      <Field.Root
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label */}
        {label && (
          <Box
            as="label"
            fontSize="sm"
            fontWeight="medium"
            color={invalid ? '#EF4444' : '#374151'}
            display="block"
            marginBottom="4px"
          >
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Switch with description in horizontal layout */}
        <HStack justifyContent="space-between" alignItems="flex-start" gap={4}>
          <Box flex="1">
            {/* Description */}
            {description && (
              <Box fontSize="sm" color="#4B5563" marginBottom={description && !label ? 0 : 1}>
                {description}
              </Box>
            )}
          </Box>

          {/* Switch Component */}
          <ChakraSwitch.Root
            id={switchId}
            disabled={disabled}
            defaultChecked={defaultChecked}
            checked={checked}
            onCheckedChange={(details: { checked: boolean }) => onChange?.(details.checked)}
            {...filteredProps}
          >
            <ChakraSwitch.HiddenInput ref={_ref} />
            <ChakraSwitch.Control {...switchControlStyle}>
              <ChakraSwitch.Thumb {...switchThumbStyle} />
            </ChakraSwitch.Control>
          </ChakraSwitch.Root>
        </HStack>

        {/* Error Message */}
        {error && (
          <Box fontSize="xs" color="#EF4444" marginTop="4px">
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="xs" color="#4B5563" marginTop="4px">
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

Switch.displayName = 'Switch';

// Export composable components for advanced usage
export const SwitchRoot = ChakraSwitch.Root;
export const SwitchControl = ChakraSwitch.Control;
export const SwitchThumb = ChakraSwitch.Thumb;
export const SwitchHiddenInput: React.ComponentType = ChakraSwitch.HiddenInput;

export { Switch };
